import React from "react";
import { useIonViewDidEnter, useIonViewWillLeave } from "@ionic/react";
import wLogo from "../imgs/w-01.png";

interface HighwayCardProps {
  percentile: number;
  mpg?: number;
  fleetRank?: number;
  groupCount?: number;
  avgMpg?: number;
}

function accentColor(pct: number) {
  if (pct >= 65) return "#99cc00";
  if (pct >= 32) return "#EED202";
  if (pct >= 11) return "#EF9F27";
  return "#DE0D0D";
}

function accentGradient(pct: number): [string, string] {
  if (pct >= 65) return ["#b8e030", "#7aaa00"];
  if (pct >= 32) return ["#f1e408", "#b8860e"];
  if (pct >= 11) return ["#f5b84a", "#c97a12"];
  return ["#d30707", "#d30707"];
}

function driverMpgColor(driver: number, avg: number): string {
  const delta = driver - avg;
  if (delta <= -0.2) return "#d40000";
  if (delta < 0.2) return "#d4a000";
  return "#1B5E20";
}

interface StatCellProps {
  label: string;
  value: string;
  labelColor?: string;
  valueColor: string;
  borderRight?: boolean;
  borderBottom?: boolean;
  id?: string;
}

function StatCell({ label, value, labelColor = "#888", valueColor, borderRight, borderBottom, id }: StatCellProps) {
  return (
    <div id={id} style={{
      textAlign: "center",
      padding: "14px 12px",
      borderRight: borderRight ? "1px solid #ececec" : undefined,
      borderBottom: borderBottom ? "1px solid #ececec" : undefined,
    }}>
      <div style={{ fontSize: 14, color: labelColor, fontWeight: 700 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 700, color: valueColor, marginTop: 4 }}>{value}</div>
    </div>
  );
}

export function HighwayCard({ percentile, mpg, fleetRank, groupCount, avgMpg }: HighwayCardProps) {
  // pct = rank/total * 100 — lower means a better rank position
  const pct = Math.max(0, Math.min(100, percentile));
  // rankPct = percentile from the bottom — higher means a better rank position
  // used for colors and the truck bar so top performers get green and sit near "Top"
  const rankPct = 100 - pct;
  const rankColor = accentColor(rankPct);
  const [gradTop, gradBottom] = accentGradient(rankPct);
  const mpgValue = mpg ?? 0;
  const mpgColor = avgMpg !== undefined ? driverMpgColor(mpgValue, avgMpg) : rankColor;

  const topPercent = Math.round(pct);

  // Entrance animation: start at 0, slide to real value after mount
  const [displayPct, setDisplayPct] = React.useState(0);
  const animTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const playAnimation = React.useCallback((target: number) => {
    if (animTimerRef.current) clearTimeout(animTimerRef.current);
    setDisplayPct(0);
    animTimerRef.current = setTimeout(() => setDisplayPct(target), 80);
  }, []);

  // Fire on first mount
  React.useEffect(() => {
    playAnimation(rankPct);
    return () => { if (animTimerRef.current) clearTimeout(animTimerRef.current); };
  }, [rankPct]);

  // Re-fire every time the user navigates to this page
  useIonViewDidEnter(() => {
    playAnimation(rankPct);
  });

  // Reset truck to 0 the moment the user leaves so re-entry always animates from the start
  useIonViewWillLeave(() => {
    if (animTimerRef.current) clearTimeout(animTimerRef.current);
    setDisplayPct(0);
  });

  const fleetRankValue =
    fleetRank !== undefined && groupCount !== undefined
      ? `${fleetRank}/${groupCount}`
      : "—";

  return (
    <div id="highway-card" style={{
      background: "#fff",
      border: "0.5px solid rgba(0,0,0,0.08)",
      borderRadius: 24,
      overflow: "hidden",
      fontFamily: "Inria Sans, system-ui, sans-serif",
      width: "100%",
    }}>

      {/* Road section */}
      <div style={{ background: "#ffffff", padding: "14px 16px 16px" }} id = "fleet-rank-title">
        <div style={{ fontSize: 24, color: "rgb(0, 0, 0)", marginBottom: 12 }}>
          Fleet ranking | Where you rank
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
          <span style={{ fontSize: 20, color: "rgb(0, 0, 0)" }}>Bottom</span>
          <span style={{ fontSize: 20, color: "rgb(0, 0, 0)" }}>Top</span>
        </div>

        <div style={{
          position: "relative",
          height: 64,
          background: "linear-gradient(180deg, #212121 0%, #3b3b3b 55%, #3b3b3b 100%)",
          borderRadius: 10,
          overflow: "hidden",
          boxShadow: "inset 0 2px 6px rgba(0,0,0,0.45), inset 0 -1px 0 rgba(255,255,255,0.04)",
        }}>
          {/* Solid trail — fills left → truck, sits on the lane behind the truck */}
          <div style={{
            position: "absolute",
            top: "50%",
            left: 0,
            right: 0,
            height: 16,
            transform: "translateY(-50%)",
            zIndex: 0,
            borderRadius: 4,
            overflow: "hidden",
            boxShadow: "inset 0 2px 4px rgba(0,0,0,0.55), inset 0 -1px 0 rgba(255,255,255,0.06)",
          }}>
            <div style={{
              position: "absolute",
              inset: 0,
              background: "linear-gradient(180deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.03) 45%, rgba(0,0,0,0.15) 100%)",
            }} />
            <div style={{
              width: `${displayPct}%`,
              height: "100%",
              background: `linear-gradient(180deg, ${gradTop} 0%, ${rankColor} 55%, ${gradBottom} 100%)`,
              boxShadow: `0 0 10px ${rankColor}66, inset 0 1px 0 rgba(255,255,255,0.35), inset 0 -2px 4px rgba(0,0,0,0.2)`,
              transition: "width 0.9s cubic-bezier(0.4,0,0.2,1)",
              position: "relative",
            }}>
              <div style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "45%",
                background: "linear-gradient(180deg, rgba(255,255,255,0.28) 0%, transparent 100%)",
                pointerEvents: "none",
              }} />
            </div>
          </div>

          {[18, 44].map((top) => (
            <div key={top} style={{
              position: "absolute", top, left: 0, right: 0, height: 1, zIndex: 1,
              backgroundImage: "repeating-linear-gradient(90deg,rgba(255,255,255,.07) 0,rgba(255,255,255,.07) 14px,transparent 14px,transparent 26px)",
            }} />
          ))}

          {Array.from({ length: 30 }, (_, i) => {
            const dp = (i + 1) / 31 * 100;
            const row = i % 2 === 0 ? 18 : 44;
            return (
              <div key={i} style={{
                position: "absolute",
                left: `${dp}%`,
                top: row,
                transform: "translate(-50%, -50%)",
                width: 6, height: 6, borderRadius: "50%",
                background: dp < displayPct ? "rgba(255,255,255,0.25)" : "rgba(255,255,255,0.08)",
                transition: "background 0.9s ease",
                zIndex: 1,
              }} />
            );
          })}

          <div style={{
            position: "absolute",
            left: `${displayPct}%`,
            top: "50%",
            transform: "translate(-50%, -38%)",
            transition: "left 0.9s cubic-bezier(0.4,0,0.2,1)",
            zIndex: 2,
          }}>
            <svg width="73" height="33" viewBox="0 0 90 32" fill="none" style={{ filter: "drop-shadow(0 3px 5px rgba(0,0,0,0.45)) drop-shadow(0 1px 2px rgba(0,0,0,0.3))" }}>
              <defs>
                <linearGradient id="trailerBody" x1="0" y1="2" x2="0" y2="24" gradientUnits="userSpaceOnUse">
                  <stop offset="0%" stopColor="#fafafa" />
                  <stop offset="35%" stopColor="#ececec" />
                  <stop offset="100%" stopColor="#c8c8c8" />
                </linearGradient>
                <linearGradient id="trailerRoof" x1="0" y1="2" x2="0" y2="6" gradientUnits="userSpaceOnUse">
                  <stop offset="0%" stopColor="#ffffff" />
                  <stop offset="100%" stopColor="#d8d8d8" />
                </linearGradient>
                <linearGradient id="trailerSkirt" x1="0" y1="22" x2="0" y2="24" gradientUnits="userSpaceOnUse">
                  <stop offset="0%" stopColor="#b8b8b8" />
                  <stop offset="100%" stopColor="#8a8a8a" />
                </linearGradient>
                <linearGradient id="cabBody" x1="60" y1="4" x2="60" y2="24" gradientUnits="userSpaceOnUse">
                  <stop offset="0%" stopColor="#ffffff" />
                  <stop offset="50%" stopColor="#f0f0f0" />
                  <stop offset="100%" stopColor="#c4c4c4" />
                </linearGradient>
                <linearGradient id="cabWindow" x1="62" y1="6" x2="76" y2="16" gradientUnits="userSpaceOnUse">
                  <stop offset="0%" stopColor="#d4e8f8" />
                  <stop offset="45%" stopColor="#8eb8d4" />
                  <stop offset="100%" stopColor="#5a8aaa" />
                </linearGradient>
                <linearGradient id="sleeper" x1="74" y1="14" x2="74" y2="24" gradientUnits="userSpaceOnUse">
                  <stop offset="0%" stopColor="#f4f4f4" />
                  <stop offset="100%" stopColor="#b0b0b0" />
                </linearGradient>
                <radialGradient id="wheel" cx="0.35" cy="0.35" r="0.65">
                  <stop offset="0%" stopColor="#3a3a3a" />
                  <stop offset="55%" stopColor="#111" />
                  <stop offset="100%" stopColor="#000" />
                </radialGradient>
                <radialGradient id="hub" cx="0.4" cy="0.4" r="0.6">
                  <stop offset="0%" stopColor="#888" />
                  <stop offset="100%" stopColor="#333" />
                </radialGradient>
                <radialGradient id="headlight" cx="0.3" cy="0.3" r="0.7">
                  <stop offset="0%" stopColor="#fff9c4" />
                  <stop offset="60%" stopColor="#FFE97A" />
                  <stop offset="100%" stopColor="#e6b800" />
                </radialGradient>
              </defs>
              <rect x="0" y="2" width="58" height="22" rx="2" fill="url(#trailerBody)" />
              <rect x="0" y="2" width="58" height="4" rx="2" fill="url(#trailerRoof)" />
              <rect x="0" y="22" width="58" height="2" rx="1" fill="url(#trailerSkirt)" />
              <rect x="1" y="7" width="56" height="12" rx="1" fill="rgba(0,0,0,0.04)" />
              <image href={wLogo} x="6" y="3" width="50" height="18" preserveAspectRatio="xMidYMid meet" />
              <rect x="58" y="8" width="2" height="12" fill="#0a0a0a" />
              <rect x="60" y="4" width="20" height="20" rx="2" fill="url(#cabBody)" />
              <rect x="60.5" y="4.5" width="19" height="3" rx="1.5" fill="rgba(255,255,255,0.55)" />
              <rect x="62" y="6" width="14" height="10" rx="1" fill="url(#cabWindow)" />
              <rect x="62.5" y="6.5" width="5" height="8" rx="0.5" fill="rgba(255,255,255,0.25)" />
              <rect x="74" y="14" width="10" height="10" rx="1" fill="url(#sleeper)" />
              <rect x="75" y="20" width="8" height="4" rx="1" fill="#a8a8a8" />
              <rect x="75.5" y="20.5" width="7" height="1.5" rx="0.5" fill="rgba(255,255,255,0.3)" />
              <circle cx="76" cy="16" r="2" fill="url(#headlight)" />
              <circle cx="81" cy="16" r="2" fill="#fff" opacity={0.65} />
              <circle cx="80.5" cy="15.5" r="0.6" fill="rgba(255,255,255,0.9)" />
              {([[10, 29], [24, 29], [70, 29], [80, 29]] as const).map(([cx, cy]) => (
                <g key={`${cx}-${cy}`}>
                  <circle cx={cx} cy={cy} r={4} fill="url(#wheel)" />
                  <circle cx={cx} cy={cy} r={1.5} fill="url(#hub)" />
                  <circle cx={cx - 1} cy={cy - 1} r={0.8} fill="rgba(255,255,255,0.12)" />
                </g>
              ))}
            </svg>
          </div>
        </div>

        <div style={{
          marginTop: 10,
          background: "linear-gradient(180deg, rgba(0,0,0,0.12) 0%, rgba(0,0,0,0.06) 100%)",
          borderRadius: 4,
          height: 4,
          overflow: "hidden",
          boxShadow: "inset 0 1px 2px rgba(0,0,0,0.15)",
        }}>
          <div style={{
            width: `${displayPct}%`,
            height: "100%",
            background: `linear-gradient(180deg, ${gradTop} 0%, ${gradBottom} 100%)`,
            borderRadius: 4,
            boxShadow: `inset 0 1px 0 rgba(255,255,255,0.35)`,
            transition: "width 0.9s cubic-bezier(0.4,0,0.2,1)",
          }} />
        </div>
      </div>

      {/* 2×2 stats grid */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr" }} id="top-mileage-stats-grid">
        <StatCell
          id="stat-top-percent"
          label="You are in the top"
          value={`${topPercent}%`}
          labelColor="#000"
          valueColor={rankColor}
          borderRight
          borderBottom
        />
        <StatCell
          id="stat-mpg"
          label="MPG"
          value={mpgValue.toFixed(1)}
          labelColor="#000"
          valueColor={mpgColor}
          borderBottom
        />
        <StatCell
          id="stat-fleet-rank"
          label="Fleet Rank"
          value={fleetRankValue}
          labelColor="#000"
          valueColor="#000"
          borderRight
        />
        <StatCell
          id="stat-overall-mpg"
          label="Overall MPG"
          value={mpgValue.toFixed(2)}
          labelColor="#000"
          valueColor={mpgColor}
        />
      </div>
    </div>
  );
}
